import type { FC } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import PrivateContent from 'app/common/PrivateContent';
import BlueprintSelector from 'app/components/BlueprintSelector';
import TraderSelector from 'app/components/TraderSelector';
import TierIndicator from 'app/components/TierIndicator';
import { isMatchBooked } from 'app/utils/match';
import PandascoreVideogameFeature from 'app/common/PandascoreVideogameFeature';
import type { MatchPreviewData } from 'app/components/MatchPreview/types';
import fireIcon from 'app/icons/fire.svg';
import lightningIcon from 'app/icons/lightning_fill.svg';
import Icon from 'app/components/Icon';

import style from './style.scss';

interface Props {
  userType: UserType;
  match: MatchPreviewData;
  updating: boolean;
  onSelectBlueprint: (id: number) => void;
  onSelectTrader: (id: number, type: 'live' | 'microbetting') => void;
  onUnassignTrader: (type: 'live' | 'microbetting') => void;
}

const MatchPreviewFooter: FC<Props> = ({
  userType,
  match,
  updating,
  onSelectBlueprint,
  onSelectTrader,
  onUnassignTrader
}) => {
  const { t } = useTranslation();

  const numberOfReviewMarkets = match.review_status?.reviewed ?? null;
  const totalOfReviewMarkets = match.review_status?.total ?? null;

  // trader can edit betting group until markets are created
  // bookmakers can edit betting group until match is booked
  const isBlueprintEditable =
    userType === 'pandascore'
      ? !match.betting_metadata?.markets_created
      : !isMatchBooked(match) || ['pending', 'pre_match'].includes(match.status);

  return (
    <div className={style.footer}>
      <div
        className={classnames(style.tournamentName, {
          [style.tournamentName_withoutTier]: !match.tournament.tier
        })}
      >
        {match.tournament.tier && (
          <TierIndicator tier={match.tournament.tier} className={style.tier} />
        )}
        {match.league.name} {match.serie.full_name} {match.tournament.name}
        {match.betting_metadata?.traded_with_grid && (
          <>
            &nbsp;
            {'|'}
            &nbsp;
            <strong className={style.gridTag}>{t('common-grid')}</strong>
          </>
        )}
      </div>
      <PandascoreVideogameFeature
        videogame={match.videogame.slug}
        forceDisplay={userType === 'bookmaker'}
      >
        <div className={style.blueprint}>
          {match.betting_metadata?.blueprint && (
            <>
              {!isBlueprintEditable ? (
                match.betting_metadata.blueprint.name
              ) : (
                <BlueprintSelector
                  className={style.selector}
                  initialValue={match.betting_metadata.blueprint.id}
                  videogameSlug={match.videogame.slug}
                  disabled={updating}
                  onChange={onSelectBlueprint}
                />
              )}
              <PrivateContent access={['pandascore']}>
                <>
                  {numberOfReviewMarkets !== null &&
                    totalOfReviewMarkets !== null &&
                    totalOfReviewMarkets !== 0 && (
                      <div
                        className={classnames(style.marketsReviewed, {
                          [style.allMarketsReviewed]: numberOfReviewMarkets === totalOfReviewMarkets
                        })}
                      >
                        <p className={style.marketsReviewedText}>
                          {numberOfReviewMarkets}/{totalOfReviewMarkets}
                        </p>
                      </div>
                    )}
                </>
              </PrivateContent>
            </>
          )}
        </div>
        <PrivateContent access={['pandascore']}>
          <>
            {match.betting_metadata?.micromarkets_enabled && (
              <div className={style.assignment}>
                <Icon href={lightningIcon} className={style.assignmentIcon} />
                <TraderSelector
                  className={style.selector}
                  alignment="end"
                  value={match.betting_metadata.assigned_micromarkets_trader_id ?? undefined}
                  onChange={value => onSelectTrader(value, 'microbetting')}
                  onUnassign={() => onUnassignTrader('microbetting')}
                />
              </div>
            )}
            {match.betting_metadata?.coverage === 'live_odds' && (
              <div className={style.assignment}>
                <Icon href={fireIcon} className={style.assignmentIcon} />
                <TraderSelector
                  className={style.selector}
                  alignment="end"
                  value={match.betting_metadata.assigned_trader_id ?? undefined}
                  onChange={value => onSelectTrader(value, 'live')}
                  onUnassign={() => onUnassignTrader('live')}
                />
              </div>
            )}
          </>
        </PrivateContent>
      </PandascoreVideogameFeature>
    </div>
  );
};

MatchPreviewFooter.displayName = 'MatchPreviewFooter';

export default MatchPreviewFooter;
